<template>
  <div>
    <div ref="customer_selector">
      <BaseSelectorField
        v-model="query_params.search"
        :multiple="multiple"
        :selectedItems="selectedItems"
        :objectName="multiple ? null : selectedItems[0]?.name"
        :placeholder="disabled ? null : `Select ${!multiple ? modelName.singular : modelName.plural}`"
        :inputClasses="inputClasses"
        :label="label"
        :disabled="disabled"
        :validation="validation"
        :hasWarning="hasWarning"
        :warningMessage="warningMessage"
        :hideMessage="hideMessage"
        :loading="loading || addLoading"
        :searching="searching"
        :required="required"
        :isVoid="isVoid"
        @open-dialog="openModal"
        @clear-selection="clearSelection"
        @focus="handleFieldFocus"
        @blur="handleFieldBlur"
        @query="query"
        @remove-item="removeItem"
        tabindex="1"
      />

      <BaseDialogSelectorList
        displayName="name"
        matchKey="id"
        :open="listVisible"
        :items="query_data?.results"
        :target="$refs.customer_selector"
        :searching="searching"
        :selectedItems="selectedItems"
        :modelName="modelName"
        :enableNew="!disableAdd"
        @selected="selectItem"
        @create="create"
        @clear="clearSelection"
        @update:open="listVisible = $event"
      />
    </div>

    <CustomerModal v-if="!disableAdd && !disableModals" :newDetails="customerData" ref="new_modal" @upserted="selectItem([$event])" />

    <BaseSelectorDialog 
      v-model="query_params.search" 
      ref="dialog" 
      :modelName="modelName" 
      :disableAdd="disableAdd"
      :loading="addLoading"
      @create="create" 
      @openNew="openNew"
      @open="dialogOpen = true"
      @close="dialogOpen = false"
    >
      <template #filters>
        <slot name="filters">
          <BaseCustomerGroupDialogSelector v-model="query_params.customer_group" disableAdd disableModals disableQuickAdd />
        </slot>
      </template>

      <template #table>
        <BaseTable
          ref="table"
          :selectMode="multiple ? 'multi' : 'single'"
          :data_="query_data"
          :fields="fields"
          :selected.sync="selectedItems"
          :defaultPageSize="query_params.page_size"
          :modelName="modelName"
          :loading="queryLoading"
          @page_change="query_params.page = $event"
          @page_size_change="query_params.page_size = $event"
          @ordering_change="query_params.ordering = $event"
          @selected="() => { if (!multiple) $refs.dialog.hide() }"
          :rowClickable="!multiple"
        />
      </template>
    </BaseSelectorDialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import BaseDialogSelector from "@/components/shared/BaseDialogSelectors/BaseDialogSelector.vue";
import CustomerModal from "@/router/views/customers/CustomerModal.vue";

import BaseCustomerGroupDialogSelector from "@/components/shared/BaseDialogSelectors/BaseCustomerGroupDialogSelector.vue";

export default {
  extends: BaseDialogSelector,
  components: {
    CustomerModal,
    BaseCustomerGroupDialogSelector,
  },
  props: {
    label: { type: String, default: "Customer", },
  },
  computed: {
    customerData() {
      const keywords = [
        "inc", "ltd", "pty", "llc", "co", "corp", "company", "corporation", 
        "plc", "group", "holdings", "solutions", "enterprises", "industries", 
        "partners", "associates", "services", "systems", "global", "international", 
        "ventures", "resources", "trust", "capital", "equity", "technologies", 
        "investments", "innovations", "development", "and", "&", "the", "of", "for", 
        "this", "management", "consultancy", "marketing", "logistics", "freight", 
        "trading", "supply", "distribution", "communications", "networks", "media", 
        "creative", "design", "production", "productions", "manufacturing", "fabrication", 
        "engineering", "limited", "llp", "gmbh", "ag", "sa", "bv", "nv", "sarl", 
        "ooo", "zrt", "kk"
      ];

      const inputValue = this.query_params.search ? this.query_params.search.trim() : "";
      
      const result = {
        first_name: null,
        last_name: null,
        contact_number: null,
        email: null,
        is_company: false,
      };
      
      if (!inputValue) return result;
      
      const phoneRegex = /^[+\d()\- ]+$/;
      const emailRegex = /^[^\s@]+@[^\s@]+$/;
      
      if (phoneRegex.test(inputValue)) {
        result.contact_number = inputValue;
      } else if (emailRegex.test(inputValue)) {
        result.email = inputValue;
      } else {
        const fullName = inputValue.split(/\s+/);
        let firstName = '';
        let lastName = '';
        
        for (const word of fullName) {
          if (keywords.includes(word.toLowerCase())) {
            result.is_company = true;
            break;
          }
        }
        
        if (result.is_company) {
          firstName = inputValue;
        } else {
          if (fullName.length === 1) {
            firstName = fullName[0];
          } else {
            lastName = fullName[fullName.length - 1];
            let lastNameParts = [];
            for (let i = fullName.length - 2; i > 0; i--) {
              if (fullName[i] === fullName[i].toLowerCase()) {
                lastNameParts.unshift(fullName[i]);
              } else {
                break;
              }
            }
            if (lastNameParts.length > 0) {
              lastName = lastNameParts.join(' ') + ' ' + lastName;
            }
            firstName = fullName.slice(0, fullName.length - lastNameParts.length - 1).join(' ');
          }
        }
        result.first_name = firstName.trim();
        result.last_name = lastName ? lastName.trim() : undefined;
      }
      
      return result;
    }
  },
  data() {
    return {
      fields: [
        { key: "first_name", sortable: true },
        { key: "last_name", sortable: true },
        "contact_number",
      ],
      modelName: { singular: "Customer", plural: "Customers" },
    };
  },
  methods: {
    ...mapActions("customers", ["fetchCustomers"]),

    openNew(e) {
      const url = this.$router.resolve('/customers/customer/edit').href;
      window.open(url, '_blank');
    },

    query() {
      this.queryLoading = true;
      this.searching = true;
      this.fetchCustomers({
          ...this.query_params,
      }).then((res) => {
          this.query_data = res.data;
      }).finally(() => {
        this.queryLoading = false;
          this.searching = false;
      });
    },
  },
};
</script>